import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, LOGO_URL, EXPERTISES_DATA, SECTEURS_DATA } from "@/lib/motion";
import { BookingButton } from "@/components/BookingButton";

const DROPDOWNS = {
  expertises: EXPERTISES_DATA.map((e) => ({ to: `/expertise/${e.slug}`, label: e.navLabel })),
  secteurs: SECTEURS_DATA.map((s) => ({ to: `/secteurs#${s.id}`, label: s.navLabel })),
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileOpen(null);
  }, [pathname]);

  const LIGHT_HERO = ["/a-propos", "/observatoire", "/observatoire/newsletter", "/admin/reservations", "/desabonnement"];
  const lightHero = LIGHT_HERO.includes(pathname);
  const darkText = lightHero && !scrolled;

  const idleColor = darkText ? "text-[#0A0A0A]/60 hover:text-[#0A0A0A]" : "text-[#F9F6F0]/70 hover:text-[#F9F6F0]";
  const brandColor = darkText ? "text-[#0A0A0A]" : "text-[#F9F6F0]";

  const isActive = (l) => pathname === l.to || (l.dropdown && DROPDOWNS[l.dropdown].some((d) => pathname === d.to.split("#")[0] && pathname !== "/"));

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 border-b ${
        scrolled ? "bg-[#0A0A0A]/80 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src={LOGO_URL} alt="HERUS Axiom" className="h-16 w-auto" />
          <span className={`hidden sm:inline font-axiom text-lg tracking-[0.35em] uppercase ${brandColor}`}>Axiom</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
          {NAV_LINKS.map((l) => (
            <div
              key={l.to}
              className="relative"
              onMouseEnter={() => setHovered(l.dropdown || null)}
              onMouseLeave={() => setHovered(null)}
            >
              <Link
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`link-underline flex items-center gap-1.5 whitespace-nowrap text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300 ${
                  isActive(l) ? "text-[#C5A059]" : idleColor
                }`}
              >
                {l.label}
                {l.dropdown && <ChevronDown size={13} className={`transition-transform duration-300 ${hovered === l.dropdown ? "rotate-180" : ""}`} />}
              </Link>

              {l.dropdown && (
                <AnimatePresence>
                  {hovered === l.dropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.25, ease: [0.19, 1, 0.22, 1] }}
                      data-testid={`dropdown-${l.dropdown}`}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-72"
                    >
                      <div className="bg-[#0A0A0A]/95 backdrop-blur-xl border border-[#C5A059]/25 py-2">
                        {DROPDOWNS[l.dropdown].map((d) => (
                          <Link
                            key={d.to}
                            to={d.to}
                            data-testid={`dropdown-item-${d.to.replace(/[/#]/g, "-")}`}
                            className="block px-5 py-3 text-sm text-[#F9F6F0]/70 hover:text-[#C5A059] hover:bg-white/5 transition-colors duration-300"
                          >
                            {d.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          ))}
          <BookingButton
            label="Réserver 30 min"
            testId="nav-booking-btn"
            iconSize={14}
            className="hidden xl:inline-flex items-center gap-2 whitespace-nowrap border border-[#C5A059] text-[#C5A059] text-xs uppercase tracking-[0.2em] font-medium px-4 py-2.5 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors duration-500"
          />
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden p-2 ${darkText ? "text-[#0A0A0A]" : "text-[#F9F6F0]"}`}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
            className="lg:hidden overflow-hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <div key={l.to}>
                  <div className="flex items-center justify-between">
                    <Link
                      to={l.to}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="font-serif-display text-2xl text-[#F9F6F0]"
                    >
                      {l.label}
                    </Link>
                    {l.dropdown && (
                      <button
                        onClick={() => setMobileOpen((m) => (m === l.dropdown ? null : l.dropdown))}
                        aria-label="Sous-menu"
                        className="p-2 text-[#C5A059]"
                      >
                        <ChevronDown size={20} className={`transition-transform duration-300 ${mobileOpen === l.dropdown ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {l.dropdown && (
                    <AnimatePresence>
                      {mobileOpen === l.dropdown && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35 }}
                          className="overflow-hidden pl-4 mt-3 flex flex-col gap-3 border-l border-[#C5A059]/30"
                        >
                          {DROPDOWNS[l.dropdown].map((d) => (
                            <Link key={d.to} to={d.to} className="text-sm text-[#F9F6F0]/70 hover:text-[#C5A059] transition-colors">
                              {d.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
              <BookingButton testId="mobile-nav-booking-btn" className="mt-4 inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#0A0A0A] text-xs uppercase tracking-[0.2em] px-6 py-4" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
